import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { resetPassword, signIn } from '@/firebase/auth';
import { AuthError, AuthErrorCodes, User as FirebaseUser } from 'firebase/auth';
import { User } from '@/types/User';
import { doc, getDoc } from 'firebase/firestore';
import firestore from '@/firebase/firestore';
import { clearImpersonatedUid } from '@/helpers/impersonation';

interface UserInfo extends FirebaseUser {
  userInfo?: User;
}

interface AuthState {
  user: UserInfo | null;
  loginPending: boolean;
  loginError: string | null;
  tableView?: 'list' | 'tiles';
}

const initialState: AuthState = {
  user: null,
  loginPending: false,
  loginError: null
};

export const signInUser = createAsyncThunk(
  'auth/signInUser',
  async ({ email, password }: { email: string; password: string }, thunkAPI) => {
    try {
      const response = await signIn(email, password);
      const userInfo = await getDoc(doc(firestore, 'users', response.user.uid));
      if (userInfo.exists() && !userInfo.data().deletedAt) {
        return {
          ...response.user,
          userInfo: userInfo.data() as User
        };
      } else {
        return thunkAPI.rejectWithValue('User not found.');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e.code || 'Unknown error occurred. Please try again later.');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email, onSuccess }: { email: string; onSuccess: () => void }, thunkAPI) => {
    try {
      await resetPassword(email);
      return thunkAPI.fulfillWithValue(onSuccess);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      return thunkAPI.rejectWithValue(e as AuthError);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Payload action is used to skip navigation to '/' on logout
    logout(state, action: PayloadAction<boolean | undefined>) {
      clearImpersonatedUid();
      state.user = null;
      state.loginError = null;
      state.loginPending = false;
      if (!action.payload) {
        // Navigate to '/'
        window.location.href = '/';
      }
    },
    reset(state) {
      state.loginError = null;
      state.loginPending = false;
    },
    setUserInfo(state, action: PayloadAction<User>) {
      if (state.user) {
        state.user.userInfo = action.payload;
      }
    },
    setTableView(state, action: PayloadAction<'list' | 'tiles'>) {
      state.tableView = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(
      signInUser.rejected,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state, action: PayloadAction<any>) => {
        state.loginError = action.payload || 'Unknown error occurred. Please try again later.';
        state.loginPending = false;
        state.user = null;
      }
    );
    builder.addCase(signInUser.fulfilled, (state, action) => {
      state.loginPending = false;
      state.loginError = null;
      state.user = action.payload;
    });
    builder.addCase(signInUser.pending, (state) => {
      state.user = null;
      state.loginError = null;
      state.loginPending = true;
    });
    builder.addCase(forgotPassword.fulfilled, (state, action) => {
      state.loginPending = false;
      state.loginError = null;
      setTimeout(() => {
        action.payload();
      }, 100);
    });
    builder.addCase(forgotPassword.pending, (state) => {
      state.loginPending = true;
      state.loginError = null;
    });
    builder.addCase(forgotPassword.rejected, (state, action) => {
      const firebaseError = action.payload as AuthError;
      state.loginPending = false;
      state.loginError =
        firebaseError.code === AuthErrorCodes.USER_DELETED
          ? 'Could not find any user with this email.'
          : 'Unknown error occurred. Please try again later.';
    });
  }
});

export const { logout, reset, setUserInfo, setTableView } = authSlice.actions;

export default authSlice.reducer;
